// src/Components/Admin/SettingsMainLogo.js

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';

import { MenuUpload } from '../../assets/icons/CustomIconsAdd2';

const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';
const LIGHT_BLUE_BG = '#E3F2FD';
const BASE_HEIGHT = 48; // 100% Scale Reference Height

// LOGO UPLOAD ICON SETTINGS
const LOGO_UPLOAD_SCALE = 6.9;
const LOGO_UPLOAD_ICON_X = 18;
const LOGO_UPLOAD_ICON_Y = 18;
const LOGO_UPLOAD_TEXT_SIZE = '20px';
const LOGO_UPLOAD_TEXT_Y = 110;

export default function SettingsMainLogo({
  logoUrl,
  logoScale,
  handleImportClick,
  fileInputRef,
  handleFileChange,
  handleEraserClick,
  handleScaleChange,
  incrementScale,
  decrementScale,
}) {
  const computedHeight = (BASE_HEIGHT * logoScale) / 100;

  return (
    <>
      <Typography sx={{ color: '#000000', fontWeight: 'normal', mb: 0.5 }}>
        Main Logo
      </Typography>

      <Paper
        variant='outlined'
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          p: 1,
          borderColor: ORANGE_COLOR,
          borderWidth: '1px',
          borderRadius: 1,
          backgroundColor: 'transparent',
        }}>
        {/* Left Hand Side Icons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            mr: 3,
            borderRight: `1px solid ${ORANGE_COLOR}`,
            pr: 2,
            gap: 2,
            flexShrink: 0,
          }}>
          <IconButton onClick={handleImportClick} sx={{ p: 0, borderRadius: 2 }}>
            <MenuUpload
              sx={{ fontSize: 65, overflow: 'visible' }}
              iconScale={LOGO_UPLOAD_SCALE}
              textSize={LOGO_UPLOAD_TEXT_SIZE}
              textY={LOGO_UPLOAD_TEXT_Y}
              iconX={LOGO_UPLOAD_ICON_X}
              iconY={LOGO_UPLOAD_ICON_Y}
              label='Upload'
            />
          </IconButton>
          <input
            type='file'
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept='image/*'
            onChange={handleFileChange}
          />
        </Box>

        {/* Middle: Image Container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '4px',
              boxShadow: '0 0 4px rgba(0,0,0,0.1)',
              border: `1px solid ${ORANGE_COLOR}`,
              width: 'auto',
              height: 'auto',
              position: 'relative',
            }}>
            {logoUrl ? (
              <>
                <IconButton
                  size='small'
                  onClick={handleEraserClick}
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    backgroundColor: 'white',
                    color: '#f44336',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: '#f44336',
                      color: 'white',
                    },
                  }}>
                  <CloseIcon fontSize='small' />
                </IconButton>
                <img
                  src={logoUrl}
                  alt='Main Logo'
                  style={{
                    height: `${computedHeight}px`,
                    width: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </>
            ) : (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ minWidth: '100px', textAlign: 'center' }}>
                No Logo
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: Scale Controls */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '140px',
            minWidth: '140px',
            flexShrink: 0,
            borderLeft: `1px solid ${ORANGE_COLOR}`,
            pl: 2,
            ml: 3,
          }}>
          <Typography
            variant='caption'
            sx={{ mb: 0.5, fontWeight: 'normal', color: '#000' }}>
            Size %
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Size % TextField */}
            <TextField
              value={logoScale}
              onChange={handleScaleChange}
              type='number'
              variant='standard'
              size='small'
              InputProps={{
                disableUnderline: true,
                style: {
                  fontSize: '0.8rem',
                  fontWeight: 'normal',
                  textAlign: 'center',
                  color: DARK_BLUE,
                },
              }}
              inputProps={{
                style: { textAlign: 'center', padding: 0 },
                min: 10,
                max: 500,
              }}
              sx={{
                width: '60px',
                backgroundColor: LIGHT_BLUE_BG,
                px: 1,
                borderRadius: 1,
                border: `1px solid ${ORANGE_COLOR}`,
                '& .MuiInputBase-input': {
                  paddingTop: '4px',
                  paddingBottom: '5px',
                },
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                  {
                    display: 'none',
                  },
                '&:hover': { backgroundColor: LIGHT_BLUE_BG },
                '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
              }}
            />

            {/* Stacked Arrows */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '30px',
              }}>
              <IconButton
                onClick={incrementScale}
                sx={{
                  p: 0,
                  lineHeight: 0,
                  height: '15px',
                  width: '24px',
                  borderRadius: 0,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                }}>
                <ArrowDropUpIcon
                  fontSize='medium'
                  sx={{ color: '#000', transform: 'translateY(2px)' }}
                />
              </IconButton>

              <IconButton
                onClick={decrementScale}
                sx={{
                  p: 0,
                  lineHeight: 0,
                  height: '15px',
                  width: '24px',
                  borderRadius: 0,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                }}>
                <ArrowDropDownIcon
                  fontSize='medium'
                  sx={{ color: '#000', transform: 'translateY(-2px)' }}
                />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Paper>
    </>
  );
}
